export type Faq = {
  question: string
  answer: string
}

/**
 * INTERNAL NOTE — not rendered.
 * Receipt OCR (on device), receipt visibility, bank-detail visibility,
 * currency behaviour, expense deletion, the absence of expense editing, and
 * account-deletion behaviour are all VERIFIED against production.
 * Still unverified and therefore worded cautiously: admin approval of join
 * requests, name-only members and claiming, closed-kitty balances, and exactly
 * what the browser companion can do. See
 * docs/internal-pre-launch-checklist.md section 2. Do not add an answer here
 * that has not been confirmed in the app.
 */
export const faqs: Faq[] = [
  {
    question: 'What is KittyPot?',
    answer:
      'KittyPot helps groups keep track of shared expenses and works out who owes whom. KittyPot does not hold or move your money.',
  },
  {
    question: 'What is a kitty?',
    answer:
      'A kitty is one shared space for a group. It holds the members, everything they spend, and the record of who has settled up.',
  },
  {
    question: 'Where can I get KittyPot?',
    answer:
      'KittyPot is live and free to download on Google Play. The iPhone version is with Apple for review and will follow shortly. If you have been invited to a kitty, you can take part from your browser on any device in the meantime.',
  },
  {
    question: 'Is KittyPot free?',
    answer:
      'Yes. Every feature, every kitty and every member is free to use, with no subscription.',
  },
  {
    question: 'Are there limits on kitties or members?',
    answer:
      'No. You can create as many kitties as you like and add as many people as you need.',
  },
  {
    question: 'Does KittyPot hold my money?',
    answer:
      'No. KittyPot records what was spent and works out who owes whom. You settle up directly with each other, by bank transfer or cash, in your own time. KittyPot keeps the record, not the funds.',
  },
  {
    question: 'Does KittyPot send the payment for me?',
    answer:
      'No. Marking something as paid in KittyPot is a record that you paid it elsewhere. KittyPot never moves money, and it is not a bank or a payment service.',
  },
  {
    question: 'How do I invite people?',
    answer:
      'Share a link, a code or a QR to scan, or invite people straight from your contacts in the app.',
  },
  {
    question: 'Do my friends need the app?',
    answer:
      'No. Anyone you invite can open the link and take part in that kitty from their browser, on any device, without installing anything. The app gives you the full experience, including My Balance across all your kitties.',
  },
  {
    question: 'What if someone is not on KittyPot yet?',
    answer:
      'Add them by name so their share is tracked from the start. They can claim that name later when they join.',
  },
  {
    question: 'How do expenses work?',
    answer:
      'Add what was spent, choose who paid, and pick who the expense is shared with. Not every expense has to include everyone.',
  },
  {
    question: 'Can expenses be split unequally?',
    answer:
      'Yes. Split an expense equally between the people involved, or set a custom amount for each person.',
  },
  {
    question: 'Can I delete an expense?',
    answer:
      'Yes. If you have permission in that kitty, you can delete an expense, and balances are worked out again from what is left. An expense cannot be edited after it is added, so if the amount, title or split is wrong, delete it and add it again correctly. Receipt photos can be replaced where you have permission.',
  },
  {
    question: 'Can I add a receipt?',
    answer:
      'Yes. You can attach a photo of the receipt to an expense so the group can see what was actually bought.',
  },
  {
    question: 'How does receipt scanning work?',
    answer:
      'Scanning happens on your own phone. The app reads the text from the photo on the device and suggests details such as the amount, so the image is not sent anywhere for that step. Treat the suggestions as a starting point and check them before saving. Once you save the expense, the receipt is stored with it so the group can see it.',
  },
  {
    question: 'How do settlements work?',
    answer:
      'KittyPot shows clearly who needs to pay whom, and you can open a pair to see exactly which expenses make up the amount. You pay each other directly, mark it as paid, and the person receiving it confirms when it lands.',
  },
  {
    question: 'Can I use KittyPot abroad or with different currencies?',
    answer:
      'Yes. Each kitty runs in its own currency, and every expense in it is recorded in that currency. You can also set a display currency for yourself, which My Balance uses to show estimated totals. Changing it does not change the currency a kitty runs in. Converted amounts are estimates and may differ from the rate your bank uses when you settle.',
  },
  {
    question: 'What is My Balance?',
    answer:
      'My Balance brings together what you owe and what you are owed across every kitty, with per-person and per-kitty amounts.',
  },
  {
    question: 'What happens when a kitty is closed?',
    answer:
      'A closed kitty stays in your history so you can look back at it, and any amounts still outstanding keep showing in My Balance until each pair has settled.',
  },
  {
    question: 'Who can see my bank details?',
    answer:
      'Saving them is optional and you control whether they are shared. Where you share them, the other people who have joined that kitty can see them so they can pay you back. Anyone still waiting to join does not. They are never shown publicly, and KittyPot does not sell them or use them to make a payment on your behalf.',
  },
  {
    question: 'Can I delete my account?',
    answer:
      'Yes. In the app, go to Home → Settings → Support → Delete Account, or email us from the address on your account. Deleting your account removes or anonymises your account information and stops the account being used again. Some records that form part of a shared kitty’s expense or settlement history may remain so other members’ records still work, which can include a historical display name and receipts attached to shared expenses.',
  },
  {
    question: 'How is my data protected?',
    answer:
      'Only the people who have joined a kitty can see its expenses. We use encrypted connections and access controls, receipts are reached through time-limited links rather than being openly available, we do not sell your data, and this website runs no analytics or advertising trackers. Our Privacy Policy explains what we process and why.',
  },
]
